import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const KIND_NOTE = {
  flat: 'Billed automatically each month alongside rent.',
  metered: 'Rate per unit — billed when you enter a reading.',
  shared: 'One bill for the building, split across units.',
};

/**
 * Recurring charges attached to a unit — garbage, service charge, security.
 *
 * Flat monthly charges are materialised into recurring_invoice_rules and raised
 * by the existing generator alongside rent. Metered and shared charges are stored
 * here as a rate but not auto-raised: they need a reading or a period bill total.
 */
const UnitCharges = ({ propertyId }) => {
  const [charges, setCharges] = useState([]);
  const [types, setTypes] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ charge_type_id: '', amount: '' });

  const load = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([
        axios.get(`/api/charges/unit/${propertyId}`),
        axios.get('/api/charges/types'),
      ]);
      setCharges(c.data.charges || []);
      setUnavailable(Boolean(c.data.unavailable));
      setTypes((t.data.types || []).filter((x) => x.code !== 'rent'));
    } catch {
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.charge_type_id || form.amount === '') {
      toast.error('Pick a charge and enter an amount');
      return;
    }
    try {
      await axios.post(`/api/charges/unit/${propertyId}`, {
        charge_type_id: Number(form.charge_type_id),
        amount: Number(form.amount),
      });
      toast.success('Charge saved');
      setForm({ charge_type_id: '', amount: '' });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save charge');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from this unit?\n\nInvoices already raised are not affected.`)) return;
    try {
      await axios.delete(`/api/charges/unit/${propertyId}/${c.id}`);
      setCharges((xs) => xs.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove charge');
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-neutral-200/70 bg-white p-6">
      <div className="h-4 w-32 animate-pulse rounded bg-neutral-100" />
    </div>;
  }

  const selectedType = types.find((t) => String(t.id) === String(form.charge_type_id));

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200/70 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3 sm:px-6">
        <div>
          <h3 className="text-sm font-medium text-neutral-900">Recurring charges</h3>
          <p className="text-xs text-neutral-500">Charged to this unit on top of rent</p>
        </div>
        {!unavailable && !adding && (
          <button type="button" onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-2.5 py-1.5 text-xs font-medium text-neutral-700 transition hover:border-primary-600 hover:text-primary-700">
            <Plus className="h-3.5 w-3.5" /> Add charge
          </button>
        )}
      </div>

      {unavailable ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500 sm:px-6">
          Run <code className="font-mono text-xs">database/charges_migration.sql</code> to enable named charges.
        </p>
      ) : (
        <>
          {adding && (
            <form onSubmit={add} className="flex flex-wrap items-end gap-3 border-b border-neutral-100 bg-neutral-50/60 px-4 py-4 sm:px-6">
              <div className="min-w-[10rem] flex-1">
                <label htmlFor="charge_type" className="mb-1 block text-xs font-medium text-neutral-700">Charge</label>
                <select id="charge_type" value={form.charge_type_id}
                  onChange={(e) => setForm({ ...form, charge_type_id: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-100">
                  <option value="">Select…</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="w-36">
                <label htmlFor="charge_amount" className="mb-1 block text-xs font-medium text-neutral-700">
                  {selectedType?.billing_kind === 'metered' ? `Rate per ${selectedType.unit_label || 'unit'}` : 'Amount / month'}
                </label>
                <input id="charge_amount" type="number" min="0" step="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm tabular-nums focus:border-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-100" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="rounded-lg bg-primary-700 px-3 py-2 text-sm font-medium text-white hover:bg-primary-800">Save</button>
                <button type="button" onClick={() => { setAdding(false); setForm({ charge_type_id: '', amount: '' }); }}
                  className="rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 hover:text-neutral-900">Cancel</button>
              </div>
              {selectedType && (
                <p className="w-full text-xs text-neutral-500">{KIND_NOTE[selectedType.billing_kind]}</p>
              )}
            </form>
          )}

          {charges.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-neutral-500 sm:px-6">
              No extra charges on this unit — rent only.
            </p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {charges.map((c) => (
                <li key={c.id} className="group flex items-center gap-3 px-4 py-3 sm:px-6">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-900">{c.name}</p>
                    <p className="text-xs text-neutral-500">{KIND_NOTE[c.billing_kind]}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-medium tabular-nums text-neutral-900">{formatCurrency(c.amount)}</p>
                    {c.billing_kind === 'metered' && c.unit_label && (
                      <p className="text-xs text-neutral-500">per {c.unit_label}</p>
                    )}
                  </div>
                  <button type="button" onClick={() => remove(c)} title={`Remove ${c.name}`}
                    className="shrink-0 rounded p-1 text-neutral-400 opacity-0 transition hover:bg-danger-50 hover:text-danger-600 group-hover:opacity-100 focus:opacity-100">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default UnitCharges;
